import type { DbExecutor } from "../executor";
import { generateId } from "../ids";
import {
  mileageAmountCents,
  rateForDate,
  validateMileageInput,
  type MileageEntry,
  type MileageEntryInput,
  type MileageRate,
} from "../../domain/mileage";
import type { FinancialBucket } from "../../domain/buckets";
import { ValidationError } from "./projectRepository";

interface RateRow {
  id: string;
  effective_from: string;
  cents_per_mile: number;
  note: string;
  created_at: string;
}

interface EntryRow {
  id: string;
  project_id: string;
  date: string;
  purpose: string;
  start_location: string;
  destination: string;
  miles_x10: number;
  rate_cents_per_mile: number;
  amount_cents: number;
  bucket: string;
  notes: string;
  voided_at: string | null;
  created_at: string;
  updated_at: string;
}

function mapEntry(r: EntryRow): MileageEntry {
  return {
    id: r.id,
    projectId: r.project_id,
    date: r.date,
    purpose: r.purpose,
    startLocation: r.start_location,
    destination: r.destination,
    milesX10: Number(r.miles_x10),
    rateCentsPerMile: Number(r.rate_cents_per_mile),
    amountCents: Number(r.amount_cents),
    bucket: r.bucket as FinancialBucket,
    notes: r.notes,
    voidedAt: r.voided_at,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export class MileageRepository {
  constructor(private readonly db: DbExecutor) {}

  /* ---------- Rates (user-configured; nothing seeded) ---------- */

  async listRates(): Promise<MileageRate[]> {
    const rows = await this.db.select<RateRow>(
      "SELECT * FROM mileage_rates ORDER BY effective_from DESC",
    );
    return rows.map((r) => ({
      id: r.id,
      effectiveFrom: r.effective_from,
      centsPerMile: Number(r.cents_per_mile),
      note: r.note,
      createdAt: r.created_at,
    }));
  }

  async addRate(effectiveFrom: string, centsPerMile: number, note = ""): Promise<MileageRate> {
    if (effectiveFrom.trim() === "") throw new ValidationError(["Effective date is required."]);
    if (!Number.isSafeInteger(centsPerMile) || centsPerMile < 0) {
      throw new ValidationError(["Rate must be a valid non-negative amount per mile."]);
    }
    const rate: MileageRate = {
      id: generateId(),
      effectiveFrom,
      centsPerMile,
      note,
      createdAt: new Date().toISOString(),
    };
    await this.db.execute(
      "INSERT INTO mileage_rates (id, effective_from, cents_per_mile, note, created_at) VALUES (?, ?, ?, ?, ?)",
      [rate.id, rate.effectiveFrom, rate.centsPerMile, rate.note, rate.createdAt],
    );
    return rate;
  }

  async removeRate(id: string): Promise<void> {
    await this.db.execute("DELETE FROM mileage_rates WHERE id = ?", [id]);
  }

  /** Default rate for a trip date (latest effective ≤ date), or null. */
  async defaultRateFor(date: string): Promise<MileageRate | null> {
    return rateForDate(await this.listRates(), date);
  }

  /* ---------- Entries ---------- */

  async listByProject(projectId: string): Promise<MileageEntry[]> {
    const rows = await this.db.select<EntryRow>(
      "SELECT * FROM mileage_entries WHERE project_id = ? ORDER BY date DESC, created_at DESC",
      [projectId],
    );
    return rows.map(mapEntry);
  }

  async listAll(): Promise<MileageEntry[]> {
    const rows = await this.db.select<EntryRow>(
      "SELECT * FROM mileage_entries ORDER BY date DESC, created_at DESC",
    );
    return rows.map(mapEntry);
  }

  async create(projectId: string, input: MileageEntryInput): Promise<MileageEntry> {
    const problems = validateMileageInput(input);
    if (problems.length > 0) throw new ValidationError(problems);
    const now = new Date().toISOString();
    const entry: MileageEntry = {
      id: generateId(),
      projectId,
      date: input.date,
      purpose: input.purpose.trim(),
      startLocation: input.startLocation.trim(),
      destination: input.destination.trim(),
      milesX10: input.milesX10,
      rateCentsPerMile: input.rateCentsPerMile, // FROZEN snapshot
      amountCents: mileageAmountCents(input.milesX10, input.rateCentsPerMile),
      bucket: input.bucket,
      notes: input.notes,
      voidedAt: null,
      createdAt: now,
      updatedAt: now,
    };
    await this.db.execute(
      `INSERT INTO mileage_entries (id, project_id, date, purpose, start_location, destination,
         miles_x10, rate_cents_per_mile, amount_cents, bucket, notes, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        entry.id, entry.projectId, entry.date, entry.purpose, entry.startLocation,
        entry.destination, entry.milesX10, entry.rateCentsPerMile, entry.amountCents,
        entry.bucket, entry.notes, entry.createdAt, entry.updatedAt,
      ],
    );
    return entry;
  }

  async update(id: string, input: MileageEntryInput): Promise<void> {
    const problems = validateMileageInput(input);
    if (problems.length > 0) throw new ValidationError(problems);
    await this.db.execute(
      `UPDATE mileage_entries SET date = ?, purpose = ?, start_location = ?, destination = ?,
         miles_x10 = ?, rate_cents_per_mile = ?, amount_cents = ?, bucket = ?, notes = ?, updated_at = ?
       WHERE id = ?`,
      [
        input.date, input.purpose.trim(), input.startLocation.trim(), input.destination.trim(),
        input.milesX10, input.rateCentsPerMile,
        mileageAmountCents(input.milesX10, input.rateCentsPerMile),
        input.bucket, input.notes, new Date().toISOString(), id,
      ],
    );
  }

  async void(id: string): Promise<void> {
    const now = new Date().toISOString();
    await this.db.execute(
      "UPDATE mileage_entries SET voided_at = ?, updated_at = ? WHERE id = ? AND voided_at IS NULL",
      [now, now, id],
    );
  }

  async unvoid(id: string): Promise<void> {
    await this.db.execute(
      "UPDATE mileage_entries SET voided_at = NULL, updated_at = ? WHERE id = ?",
      [new Date().toISOString(), id],
    );
  }
}
